import React, { useState } from 'react'
import { Alert, Button, Card, Form, Input, message, Radio, Typography } from 'antd'
import { StatusDefinition, statusLabel, toneOf } from '../../domain/Status'
import WallexService from '../../service/WallexService'
import { capitalize } from '../../util/format'
import StatusJourney from './StatusJourney'
import StatusTag, { ToneIcon } from './StatusTag'
const { Text } = Typography

interface StatusChangerProps {
    // e.g. 'funding', used in messages
    noun: string
    current: string
    // Domain statuses for the entity, including the current one
    statuses: StatusDefinition[]
    journey?: string[]
    hints?: Record<string, string>
    reasonLabel?: string
    // Shown above the status options, e.g. warnings about the next step
    notice?: React.ReactNode
    // Resolves once the change is saved; the page then updates `current`
    onSubmit: (target: string, reason?: string) => Promise<unknown>
}

interface StatusChangeValues {
    target?: string
    reason?: string
}

// Pick the next status, then confirm. The page updates `current` once the change is saved
const StatusChanger: React.FC<StatusChangerProps> = ({ noun, current, statuses, journey = [], hints = {}, reasonLabel = 'Reason', notice, onSubmit }) => {
    const [form] = Form.useForm<StatusChangeValues>()
    const target = Form.useWatch('target', form)
    const [submitting, setSubmitting] = useState<boolean>(false)
    const [error, setError] = useState<string | null>(null)
    const [messageApi, messageContext] = message.useMessage()

    const labelOf = (status: string) => statusLabel(status, statuses)
    const options = statuses.filter((status) => status.value !== current)
    // A pick that is no longer offered (e.g. after a refresh) counts as no pick
    const selected = options.find((status) => status.value === target)

    const onFinish = (values: StatusChangeValues) => {
        const next = options.find((status) => status.value === values.target)
        if (!next) return

        setSubmitting(true)
        setError(null)
        onSubmit(next.value, next.needReason ? values.reason?.trim() : undefined)
            .then(() => {
                form.resetFields()
                void messageApi.success(`${capitalize(noun)} moved to ${next.description}`)
            })
            .catch((e) => setError(WallexService.errorDescription(e, `Couldn’t move the ${noun} to ${next.description}.`)))
            .finally(() => setSubmitting(false))
    }

    return (
        <Card title="Change status" className="status-changer">
            {messageContext}
            <div className="status-current">
                <Text type="secondary">Currently</Text>
                <StatusTag status={current} label={labelOf(current)} />
                {hints[current] && <Text type="secondary">{hints[current]}</Text>}
            </div>

            <StatusJourney journey={journey} current={current} labelOf={labelOf} />

            {notice && <div className="status-notice">{notice}</div>}

            {/* The form stays mounted so it can always be reset */}
            <Form form={form} layout="vertical" requiredMark={false} onFinish={onFinish}>
                {options.length === 0 ? (
                    <Alert type="info" showIcon message={`No status changes are available from ${labelOf(current)} in the simulator.`} />
                ) : (
                    <>
                        <Form.Item name="target" label="Move to" rules={[{ required: true, message: 'Pick a status' }]}>
                            <Radio.Group className="status-options">
                                {options.map((option) => (
                                    <Radio key={option.value} value={option.value} className={`status-option status-option-${toneOf(option.value)}`}>
                                        <span className="status-option-title">
                                            <ToneIcon status={option.value} />
                                            {option.description}
                                        </span>
                                        {hints[option.value] && <span className="status-option-hint">{hints[option.value]}</span>}
                                    </Radio>
                                ))}
                            </Radio.Group>
                        </Form.Item>

                        {selected?.needReason && (
                            <Form.Item name="reason" label={reasonLabel} rules={[{ required: true, whitespace: true, message: `${reasonLabel} is required` }]}>
                                <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} placeholder="Add a short explanation" />
                            </Form.Item>
                        )}

                        {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}

                        <Button
                            type="primary"
                            size="large"
                            htmlType="submit"
                            loading={submitting}
                            disabled={!selected}
                            danger={!!selected && toneOf(selected.value) === 'error'}
                        >
                            {selected ? `Move to ${selected.description}` : 'Pick a status above'}
                        </Button>
                    </>
                )}
            </Form>
        </Card>
    )
}

export default StatusChanger
